import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from '@mui/material';

import {
  IArnoMigration,
  IArnoMigrationIssue,
  IArnoRecheck,
  useRecheckArnoMigration,
  useResolveArnoMigrationField,
} from '../../hooks/reactQuerys/ArnoAdminQueries';
import {
  ifClassLabel,
  migrationSummary,
  onlyMappingMissing,
  orderedGroups,
  readableFieldCode,
  reasonLabel,
  resolvableVariables,
  wordIfDetail,
} from './migrationModel';
import { TechnicalDetails } from './TechnicalDetails';
import { SelectorPicker } from './SelectorPicker';
import { useAlert } from '../../context/AlertContext';

/**
 * Migrationsübersicht eines kopierten Baums (#1245).
 *
 * Ein aus ArnoTop kopierter Baum bringt Formatierung mit, aber nicht jede
 * Fachlogik lässt sich übersetzen. Was offen ist, steht hier — gruppiert nach
 * Grund, mit echten Zahlen, mit Sprung zur betroffenen Stelle, und für den
 * häufigsten Fall (eine ungemappte Legacy-Variable) mit einem Weg, ihn direkt
 * aufzulösen.
 *
 * Die globale ArnoTop-Zuordnung bleibt dabei unberührt: eine Entscheidung in
 * diesem Baum ist eine Entscheidung in diesem Baum.
 */

interface IProps {
  migration?: IArnoMigration;
  idDecisionTemplateVersion: number;
  editable: boolean;
  onJumpToSection: (sectionId: string) => void;
  onResolved: () => void;
}

export const MigrationPanel: React.FC<IProps> = ({
  migration,
  idDecisionTemplateVersion,
  editable,
  onJumpToSection,
  onResolved,
}) => {
  const { showAlert } = useAlert();
  const { resolveMigrationField, isResolvingMigrationField } =
    useResolveArnoMigrationField();
  const { recheckMigration, isRecheckingMigration } = useRecheckArnoMigration();
  const [variable, setVariable] = useState<string | null>(null);
  const [selector, setSelector] = useState('');
  const [recheck, setRecheck] = useState<IArnoRecheck | null>(null);
  // Die Liste der offenen Stellen ist lang (hier 126) und schob den Editor aus dem ersten
  // Bildschirm. Sie steht auf Wunsch da; Zusammenfassung und Handlungen bleiben sichtbar.
  const [showDetails, setShowDetails] = useState(false);

  if (!migration || migration.open === 0) {
    return null;
  }

  const groups = orderedGroups(migration);
  const resolvable = resolvableVariables(migration);

  const resolve = async () => {
    if (!variable || !selector) return;
    try {
      await resolveMigrationField({
        idDecisionTemplateVersion,
        variable,
        selector,
      });
      showAlert({
        severity: 'success',
        text: `„${variable}" ist in diesem Baum jetzt ${selector}.`,
      });
      setVariable(null);
      setSelector('');
      onResolved();
    } catch (error) {
      showAlert({
        severity: 'error',
        text:
          error instanceof Error
            ? `Ersetzen fehlgeschlagen: ${error.message}`
            : 'Ersetzen fehlgeschlagen.',
      });
    }
  };

  // Ein bestätigtes Legacy-Mapping ändert diesen Baum NICHT von selbst. Erst hier wird
  // gerechnet, und erst der zweite Klick schreibt — der Entwurf gehört dem, der ihn bearbeitet.
  const runRecheck = async (apply: boolean) => {
    try {
      const result = await recheckMigration({
        idDecisionTemplateVersion,
        apply,
      });
      setRecheck(result);
      if (apply) {
        showAlert({
          severity: 'success',
          text: `${result.applied} offene Stellen sind jetzt DiNo-Felder.`,
        });
        onResolved();
      }
    } catch (error) {
      showAlert({
        severity: 'error',
        text:
          error instanceof Error
            ? `Prüfen fehlgeschlagen: ${error.message}`
            : 'Prüfen fehlgeschlagen.',
      });
    }
  };

  const issuesOf = (reasonCode: string): IArnoMigrationIssue[] =>
    migration.issues
      .filter(issue => issue.reasonCode === reasonCode)
      .slice(0, 8);

  return (
    <Alert severity='warning' sx={{ mb: 2 }}>
      <AlertTitle>Migration — {migrationSummary(migration)}</AlertTitle>
      <Typography variant='body2' sx={{ mb: 1 }}>
        Die Formatierung ist übernommen. Offen ist nur, was sich aus ArnoTop
        nicht sicher übersetzen ließ; solange davon etwas übrig ist, lässt sich
        der Baum nicht veröffentlichen.
      </Typography>

      {editable && (
        <Box sx={{ mb: 1 }}>
          <Button
            size='small'
            variant='outlined'
            disabled={isRecheckingMigration}
            onClick={() => runRecheck(false)}
          >
            Migration erneut prüfen
          </Button>
          {recheck && (
            <Box sx={{ mt: 1 }}>
              <Typography variant='body2'>
                {recheck.resolvablePlaces > 0
                  ? `${recheck.resolvablePlaces} offene Stellen könnten jetzt übernommen werden: ${recheck.resolvableFields
                      .map(field => `${field.variable} → ${field.selector}`)
                      .join(', ')}.`
                  : 'Keine offene Stelle lässt sich mit den bestätigten Zuordnungen lösen.'}
              </Typography>
              {recheck.conditionPlaces > 0 && (
                <Typography variant='caption' color='text.secondary'>
                  {recheck.conditionPlaces} offene Word-Bedingungen hängen
                  ebenfalls an inzwischen zugeordneten Feldern. Sie entstehen
                  erst bei einem neuen Klon wieder, weil ihre Zweige in diesem
                  Entwurf nicht mehr stehen.
                </Typography>
              )}
              {recheck.resolvablePlaces > 0 && (
                <Button
                  size='small'
                  variant='contained'
                  sx={{ mt: 1 }}
                  disabled={isRecheckingMigration}
                  onClick={() => runRecheck(true)}
                >
                  {recheck.resolvablePlaces} Stellen übernehmen
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}

      <Button
        size='small'
        onClick={() => setShowDetails(open => !open)}
        sx={{ mb: showDetails ? 1 : 0 }}
      >
        {showDetails
          ? 'Offene Stellen ausblenden'
          : `Offene Stellen anzeigen (${migration.open})`}
      </Button>

      <Stack spacing={1} sx={{ display: showDetails ? 'flex' : 'none' }}>
        {groups.map(group => (
          <Box key={group.reasonCode}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <Chip size='small' label={group.count} />
              <Typography variant='body2' fontWeight={600}>
                {reasonLabel(group.reasonCode)}
              </Typography>
            </Stack>
            <Box component='ul' sx={{ m: 0, pl: 3 }}>
              {issuesOf(group.reasonCode).map((issue, index) => {
                const wordIf = wordIfDetail(issue);
                return (
                  <li key={`${group.reasonCode}-${index}`}>
                    <Link
                      component='button'
                      type='button'
                      variant='body2'
                      underline='hover'
                      align='left'
                      sx={{ textAlign: 'left' }}
                      onClick={() =>
                        issue.sectionId && onJumpToSection(issue.sectionId)
                      }
                    >
                      {issue.sectionTitle}: {issue.message}
                    </Link>
                    {wordIf && (
                      <Box sx={{ pl: 1 }}>
                        {/*
                          Die Bedingung im Klartext, damit sie sich lesen laesst,
                          ohne im Altbestand nachzusehen. Der Feldcode steht
                          darunter als technisches Detail.
                        */}
                        <Typography variant='caption' color='text.secondary'>
                          {wordIf.variable
                            ? `Feld ${wordIf.variable}`
                            : 'Bedingung'}
                          {wordIf.operator ? ` ${wordIf.operator}` : ''}
                          {wordIf.value ? ` »${wordIf.value}«` : ''}
                          {wordIf.ifClass
                            ? ` · ${ifClassLabel(wordIf.ifClass)}`
                            : ''}
                        </Typography>
                        {onlyMappingMissing(issue) && (
                          <Typography
                            variant='caption'
                            color='success.main'
                            component='div'
                          >
                            Wird dieses Feld einer DiNo-Variable zugeordnet,
                            kann die Bedingung automatisch übernommen werden.
                          </Typography>
                        )}
                        {wordIf.fieldCode && (
                          <TechnicalDetails
                            entries={[
                              {
                                label: 'Word-Feldcode',
                                value: (
                                  <Typography
                                    variant='caption'
                                    component='div'
                                    sx={{ fontFamily: 'monospace' }}
                                  >
                                    {readableFieldCode(wordIf.fieldCode).slice(
                                      0,
                                      200
                                    )}
                                  </Typography>
                                ),
                              },
                            ]}
                          />
                        )}
                      </Box>
                    )}
                  </li>
                );
              })}
              {group.count > 8 && (
                <li>
                  <Typography variant='body2' component='span'>
                    … und {group.count - 8} weitere
                  </Typography>
                </li>
              )}
            </Box>
          </Box>
        ))}
      </Stack>

      {(migration.participantSlots ?? []).length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant='subtitle2'>
            Zuordnung derzeit nicht sicher möglich
          </Typography>
          <Typography variant='caption' color='text.secondary' component='div'>
            Diese Felder sind ArnoTop-Beteiligtenplätze. Welcher Beteiligte in
            DiNo dem Platz B1, B2 … entspricht, lässt sich nicht belegen — eine
            Zuordnung würde genau diese Annahme festschreiben. Die Stellen
            bleiben offen.
          </Typography>
          <Stack direction='row' sx={{ mt: 1, gap: 1, flexWrap: 'wrap' }}>
            {(migration.participantSlots ?? []).slice(0, 24).map(name => (
              <Chip key={name} size='small' variant='outlined' label={name} />
            ))}
            {(migration.participantSlots ?? []).length > 24 && (
              <Chip
                size='small'
                variant='outlined'
                label={`… und ${(migration.participantSlots ?? []).length - 24} weitere`}
              />
            )}
          </Stack>
        </Box>
      )}

      {editable && resolvable.length > 0 && (
        <>
          <Typography variant='subtitle2' sx={{ mt: 2 }}>
            Legacy-Variablen zuordnen
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            Gilt nur in diesem Entscheidungsbaum. Die globale ArnoTop-Zuordnung
            bleibt unverändert.
          </Typography>
          <Stack direction='row' spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
            {resolvable.slice(0, 12).map(entry => (
              <Chip
                key={entry.variable}
                label={`${entry.variable} (${entry.places})`}
                onClick={() => {
                  setVariable(entry.variable);
                  setSelector('');
                }}
                clickable
              />
            ))}
          </Stack>
        </>
      )}

      <Dialog
        open={Boolean(variable)}
        onClose={() => setVariable(null)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Legacy-Variable „{variable}" zuordnen</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 2 }}>
            Alle Stellen dieses Feldes in diesem Baum werden ersetzt. Die
            globale ArnoTop-Zuordnung bleibt unberührt.
          </Typography>
          <SelectorPicker value={selector} onChange={setSelector} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVariable(null)}>Abbrechen</Button>
          <Button
            variant='contained'
            disabled={!selector || isResolvingMigrationField}
            onClick={() => void resolve()}
          >
            In diesem Baum ersetzen
          </Button>
        </DialogActions>
      </Dialog>
    </Alert>
  );
};
